import { ISamlLoginButton } from '@nocode/types';
import { get } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { Linking, Platform, Text, TouchableHighlight, View } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { API_BASE_URL } from '@common/constants/apiEndpoint';
import { SSO_PENDING_ACTIONS_KEY } from '@common/constants/shared';
import SsoIcon from './SsoIcon';
import { getActions, getValueBinding } from '../../shared';
import createStyles from './style';

type SamlSetupState = 'unknown' | 'ready' | 'missing';

const SamlLogin: React.FC<ISamlLoginButton> = (props) => {
  const { id, data, dependencies, attributes } = props;
  const textErrorMissing = attributes?.textErrorMissing || {
    en: 'SSO is not set up for this app. Open App Settings > SSO login and save a configuration first.',
  };
  const textErrorField = attributes?.textErrorField || {
    en: 'Map both the email and Microsoft object ID columns of the app user table in the element settings.',
  };
  const appId = dependencies?.appInfor?.appId;
  const styles = createStyles(attributes);
  const bindingValue = getValueBinding(id, data, props);
  const currentUrl = window?.location?.href?.toString() || '';
  const isBuilder = useMemo(
    () =>
      Platform.OS === 'web' &&
      (currentUrl.includes('canvas') || currentUrl.includes('responsive')),
    [currentUrl]
  );
  // Both columns carry the identity: the object ID is the half that never
  // changes, and the email is the half a person recognises. Without either one
  // the backend cannot tell a returning user from a new one, so an unset
  // mapping is a setup error rather than a runtime failure.
  const isConfigured =
    !!attributes?.email?.fid && !!attributes?.object_id?.fid;
  const [samlSetupState, setSamlSetupState] =
    useState<SamlSetupState>('unknown');

  useEffect(() => {
    const fetchAppSsoState = async () => {
      if (!isBuilder || !appId) {
        setSamlSetupState('ready');
        return;
      }

      try {
        // Per-app status endpoint: answers a single boolean and never carries
        // the issuer, sign-on URL, certificate or allowed domains.
        const url = `${API_BASE_URL}/saml-app-status?appId=${encodeURIComponent(
          appId
        )}`;
        const response = await fetch(url);
        if (!response.ok) {
          setSamlSetupState('missing');
          return;
        }
        const status = await response.json();
        setSamlSetupState(status?.isEnabled ? 'ready' : 'missing');
      } catch (error) {
        setSamlSetupState('missing');
      }
    };

    fetchAppSsoState();
  }, [isBuilder, appId]);

  const onPressSaml = async () => {
    try {
      if (Platform.OS !== 'web') return;
      if (!appId || !isConfigured) return;
      if (isBuilder && samlSetupState !== 'ready') return;

      const successActionId = getActions(props, 'successActions');
      const failedActionId = getActions(props, 'failedActions');

      // The IdP round-trip reloads the page, so the chains have to survive it.
      // Both are stored and the callback picks one, which is what gives this
      // element the same success/failure split as the doda Campus element.
      await AsyncStorage.setItem(
        SSO_PENDING_ACTIONS_KEY,
        JSON.stringify({
          objectId: props.id,
          screenUuid: props?.screenUuid,
          successActionId,
          failedActionId,
          successActions: successActionId
            ? props.actions?.[successActionId]?.actions || []
            : [],
          failedActions: failedActionId
            ? props.actions?.[failedActionId]?.actions || []
            : [],
        })
      );

      const returnUrl = window?.location?.href?.toString();
      if (!returnUrl) return;

      // The element's identity, not its column mapping: the backend re-reads
      // the mapping from the screen, so a tampered link cannot redirect a
      // write to a column of the caller's choosing.
      const params = new URLSearchParams({ returnUrl });
      if (props?.screenUuid) params.set("screenUuid", props.screenUuid);
      if (props?.id) params.set("objectId", props.id);

      Linking.openURL(
        `${API_BASE_URL}/sso/saml/app/${appId}/login?${params.toString()}`
      );
    } catch (error) {
      console.log('===SamlLogin onPress ERROR', error);
    }
  };

  if (isBuilder && !isConfigured) {
    return (
      <View style={styles.errorContainer}>
        <Text style={styles.errorText}>{textErrorField?.en}</Text>
      </View>
    );
  }

  if (isBuilder && samlSetupState === 'missing') {
    return (
      <View style={styles.errorContainer}>
        <Text style={styles.errorText}>{textErrorMissing?.en}</Text>
      </View>
    );
  }

  return (
    <TouchableHighlight
      onPress={onPressSaml}
      disabled={isBuilder && samlSetupState !== 'ready'}
    >
      <View style={styles.container}>
        <View style={styles.iconContainer}>
          <SsoIcon
            color={
              attributes.type === 'contained' ? attributes.color : '#2F2F2F'
            }
          />
        </View>
        <Text style={styles.text}>{get(bindingValue, 'text')}</Text>
      </View>
    </TouchableHighlight>
  );
};

export default SamlLogin;
